import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { ContentControlToFieldMapService } from '../content-control-to-field-map.service';
import { ContentControlToSignatureFieldService } from '../esignature/content-control-to-signature-field.service';
import { LoggingService } from '../logging.service';
import { ContentControlId } from './content-control-id';
import { DocumentSelectionChangedService } from './document-selection-changed-service';

/**
 * Fires a message when the user places the caret in a ContentControl
 */
@Injectable({ providedIn: 'root' })
export class ActiveContentControlMonitor {
  private activeContentControlChangedSubject = new ReplaySubject<ContentControlId | undefined>(1);
  activeContentControlChanged$ = this.activeContentControlChangedSubject.asObservable();
  private lastContentControlId: number | null | undefined = undefined;

  constructor(
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService,
    private loggingService: LoggingService,
    documentSelectionChangedService: DocumentSelectionChangedService
  ) {
    console.log('ActiveContentControlMonitor created.');
    documentSelectionChangedService.selectionChanged$.subscribe(this.selectionChanged.bind(this));
  }

  // TODO: move this to the WordService maybe?
  /**
   * Gets the Content Control that the caret is currently in or undefined if not inside a Content Control
   * @param ctx the request context
   */
  async getActiveContentControl(ctx: Word.RequestContext): Promise<ContentControlId | undefined> {
    try {
      const currSel = ctx.document.getSelection();
      currSel.load();
      currSel.parentContentControlOrNullObject.load();
      await ctx.sync();
      const insideContentControl = !currSel.parentContentControlOrNullObject.isNullObject;
      if (insideContentControl) {
        const newContentControlId = currSel.parentContentControlOrNullObject.id;
        return {
          id: newContentControlId,
          tag: currSel.parentContentControlOrNullObject.tag,
          title: currSel.parentContentControlOrNullObject.title,
        };
      }
    } catch (err) {
      this.loggingService.error('ActiveContentControlMonitor', 'getActiveContentControl', {
        message: 'Error trying to determine active ContentControl.',
        error: err,
      });
    }
    return undefined;
  }

  private selectionChanged(eventArgs: Office.DocumentSelectionChangedEventArgs): void {
    console.debug('%cactive-content-control-monitor - document selection changed handler', 'color:#00bb00');
    Word.run(async (ctx) => {
      try {
        const currSel = ctx.document.getSelection();
        currSel.load();
        currSel.parentContentControlOrNullObject.load();
        await ctx.sync();
        const insideContentControl = !currSel.parentContentControlOrNullObject.isNullObject;
        if (insideContentControl && this.isFieldControl(currSel.parentContentControlOrNullObject)) {
          const newContentControlId = currSel.parentContentControlOrNullObject.id;
          if (this.lastContentControlId !== newContentControlId) {
            this.lastContentControlId = newContentControlId;
            const data = {
              id: newContentControlId,
              tag: currSel.parentContentControlOrNullObject.tag,
              title: currSel.parentContentControlOrNullObject.title,
            };
            this.activeContentControlChangedSubject.next(data);
          }
        } else {
          if (this.lastContentControlId !== null) {
            this.lastContentControlId = null;
            this.activeContentControlChangedSubject.next(undefined);
          }
        }
      } catch (err) {
        this.loggingService.error('ActiveContentControlMonitor', 'selectionChanged', {
          message: 'Error trying to determine active ContentControl.',
          error: err,
        });
      }
    });
  }

  private isFieldControl(contentControl: Word.ContentControl): boolean {
    return (
      this.contentControlToFieldMapService.filter([contentControl]).length > 0 ||
      this.contentControlToSignatureFieldService.filter([contentControl]).length > 0
    );
  }
}
